import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { formatAED, formatDate } from '@/lib/format';
import { MATCH_STATUS, MATCH_STATUS_COLOR, PROJECT_STATUS_COLOR } from '@/lib/constants';

export default function MatchedProjects({ contact }) {
    const { profile } = useAuth();
    const navigate = useNavigate();
    const [matches, setMatches] = useState([]);
    const [projects, setProjects] = useState([]);
    const [developers, setDevelopers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [pickProject, setPickProject] = useState('');
    const [matchStatus, setMatchStatus] = useState('Suggested');
    const [matchNotes, setMatchNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!contact?.id) return;
        setLoading(true);
        const [{ data: m }, { data: p }, { data: d }] = await Promise.all([
            supabase.from('client_project_matches').select('*').eq('contact_id', contact.id).order('created_at', { ascending: false }),
            supabase.from('developer_projects').select('*').is('deleted_at', null).order('project_name'),
            supabase.from('developers').select('id,company_name').is('deleted_at', null),
        ]);
        setMatches(m || []);
        setProjects(p || []);
        setDevelopers(d || []);
        setLoading(false);
    }, [contact?.id]);
    useEffect(() => { load(); }, [load]);

    // Suggest projects matching client's budget + emirate (not yet matched)
    const matchedProjectIds = new Set(matches.map((m) => m.project_id));
    const suggestions = projects.filter((p) => {
        if (matchedProjectIds.has(p.id)) return false;
        if (contact.emirate_preference && contact.emirate_preference !== 'Both' && p.emirate && p.emirate !== contact.emirate_preference && p.emirate !== 'Both') return false;
        if (contact.budget_aed && p.price_range_min_aed && Number(contact.budget_aed) < Number(p.price_range_min_aed) * 0.8) return false;
        if (contact.budget_aed && p.price_range_max_aed && Number(contact.budget_aed) > Number(p.price_range_max_aed) * 1.5) return false;
        return true;
    }).slice(0, 6);

    const addMatch = async (e) => {
        e.preventDefault();
        if (!pickProject) return;
        setSaving(true);
        try {
            const { error } = await supabase.from('client_project_matches').insert({
                contact_id: contact.id,
                project_id: pickProject,
                match_status: matchStatus,
                notes: matchNotes || null,
                organization_id: contact.organization_id,
            });
            if (error) throw error;
            const proj = projects.find((p) => p.id === pickProject);
            await supabase.from('pipeline_activities').insert({
                contact_id: contact.id, organization_id: contact.organization_id, agent_id: profile.id,
                activity_type: 'project_matched', description: `Matched to project: ${proj?.project_name || ''} (${matchStatus})`,
            });
            toast.success('Project matched');
            setShowAdd(false); setPickProject(''); setMatchStatus('Suggested'); setMatchNotes('');
            load();
        } catch (err) { toast.error(err.message); } finally { setSaving(false); }
    };

    const updateStatus = async (id, status) => {
        const { error } = await supabase.from('client_project_matches').update({ match_status: status }).eq('id', id);
        if (error) return toast.error(error.message);
        toast.success(`Updated to ${status}`); load();
    };

    const removeMatch = async (id) => {
        if (!window.confirm('Remove this match?')) return;
        const { error } = await supabase.from('client_project_matches').delete().eq('id', id);
        if (error) return toast.error(error.message);
        toast.success('Match removed'); load();
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="matched-projects">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Matched projects</div>
                <button onClick={() => setShowAdd((s) => !s)} className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-gray-300 hover:border-aks-gold text-[11px] font-semibold text-aks-navy" data-testid="match-project-btn"><Plus className="h-3.5 w-3.5" /> Match project</button>
            </div>

            {showAdd && (
                <form onSubmit={addMatch} className="bg-aks-gold-soft/30 border border-aks-gold/30 rounded-md p-3 mb-3 space-y-2" data-testid="match-form">
                    <select required value={pickProject} onChange={(e) => setPickProject(e.target.value)} className="w-full h-9 px-2 rounded-md border border-gray-300 text-[13px]"><option value="">Select project…</option>{projects.map((p) => { const dev = developers.find((d) => d.id === p.developer_id); return <option key={p.id} value={p.id}>{p.project_name} {dev ? `— ${dev.company_name}` : ''}</option>; })}</select>
                    <select value={matchStatus} onChange={(e) => setMatchStatus(e.target.value)} className="w-full h-9 px-2 rounded-md border border-gray-300 text-[13px]">{MATCH_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
                    <input value={matchNotes} onChange={(e) => setMatchNotes(e.target.value)} placeholder="Notes (optional)" className="w-full h-9 px-2 rounded-md border border-gray-300 text-[13px]" />
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setShowAdd(false)} className="flex-1 h-8 rounded-md border border-gray-300 text-[12px] font-medium">Cancel</button>
                        <button disabled={saving} type="submit" className="flex-1 h-8 rounded-md bg-aks-navy text-white text-[12px] font-semibold disabled:opacity-60">{saving ? 'Saving…' : 'Match'}</button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="aks-skeleton h-14 rounded-md" />)}</div>
            ) : matches.length === 0 ? (
                <div className="py-3 text-center text-[12px] text-gray-400">No projects matched yet</div>
            ) : (
                <div className="space-y-2">
                    {matches.map((m) => {
                        const p = projects.find((x) => x.id === m.project_id);
                        const dev = p ? developers.find((d) => d.id === p.developer_id) : null;
                        const sc = MATCH_STATUS_COLOR[m.match_status] || '#9CA3AF';
                        return (
                            <div key={m.id} className="flex items-center gap-2 p-2 rounded-md border border-gray-100 hover:border-aks-gold/40" data-testid={`match-${m.id}`}>
                                <div className="h-8 w-8 rounded-md bg-aks-gold/15 text-aks-navy flex items-center justify-center flex-shrink-0"><Building2 className="h-4 w-4" /></div>
                                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => p && navigate(`/developers/${p.developer_id}`)}>
                                    <div className="font-semibold text-[13px] text-aks-text truncate">{p?.project_name || 'Project removed'}</div>
                                    <div className="text-[11px] text-gray-500 truncate">{dev?.company_name || '—'} · {p?.emirate || '—'} · {p ? `${formatAED(p.price_range_min_aed)}–${formatAED(p.price_range_max_aed)}` : ''}</div>
                                </div>
                                <select value={m.match_status} onChange={(e) => updateStatus(m.id, e.target.value)} className="h-7 px-1 rounded border text-[11px] font-semibold" style={{ borderColor: `${sc}66`, color: sc }} data-testid={`match-status-${m.id}`}>
                                    {MATCH_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <button onClick={() => removeMatch(m.id)} className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-red-50 hover:text-red-600 text-gray-400"><X className="h-3.5 w-3.5" /></button>
                            </div>
                        );
                    })}
                </div>
            )}

            {suggestions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">✨ Suggested matches</div>
                    <div className="space-y-2">
                        {suggestions.slice(0, 3).map((p) => {
                            const dev = developers.find((d) => d.id === p.developer_id);
                            const ps = PROJECT_STATUS_COLOR[p.status] || '#9CA3AF';
                            return (
                                <div key={p.id} className="flex items-center gap-2 p-2 rounded-md border border-dashed border-gray-200 hover:border-aks-gold" data-testid={`suggest-${p.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-semibold text-aks-text truncate">{p.project_name}</div>
                                        <div className="text-[11px] text-gray-500 truncate">{dev?.company_name || '—'} · {p.emirate || '—'} · {formatAED(p.price_range_min_aed)}–{formatAED(p.price_range_max_aed)}</div>
                                    </div>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: `${ps}1A`, color: ps }}>{p.status}</span>
                                    <button onClick={() => { setPickProject(p.id); setShowAdd(true); }} className="h-7 px-2 rounded-md bg-aks-gold text-aks-navy text-[11px] font-semibold">+ Match</button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
